/* eslint-disable default-param-last */
/* eslint-disable no-case-declarations */
import { Reducer } from 'redux';
import produce from 'immer';
import _ from 'underscore';

import { MonitoringRequestTypes, MonitoringState, TradingMessageTypes } from './types';

const REQUEST_INITIAL_STATE = {
  loading: false,
  loaded: false,
  error: false,
};

const INITIAL_STATE: MonitoringState = {
  monitoring: {
    monitoring_status: 'accept',
    electronics_tradings: [],
    monitoring_messages: {},
    interest_lots: {},
    trading_keywords: {},
    appeals: {},
    appeals_notifications: [],
    notifications_unread: false,
    disable_notifications: false,
    electronics_tradings_total_entries: 0,
    electronics_tradings_total_pages: 0,
    monitored_source_id_list: [],
    current_page: 1,
    current_source_id: [],
    filtered_by: 'by_me',
    monitored_electronic_tradings: {},
    current_filter_type: 'all',
  },
  request: {
    monitoring_status: REQUEST_INITIAL_STATE,
    electronics_tradings: REQUEST_INITIAL_STATE,
    delete_electronic_trading: REQUEST_INITIAL_STATE,
    monitoring_messages: REQUEST_INITIAL_STATE,
    interest_lots: REQUEST_INITIAL_STATE,
    get_trading_keywords: REQUEST_INITIAL_STATE,
    get_trading_keywords_representant: REQUEST_INITIAL_STATE,
    save_trading_keywords: REQUEST_INITIAL_STATE,
    save_trading_keywords_representant: REQUEST_INITIAL_STATE,
    delete_trading_keywords: REQUEST_INITIAL_STATE,
    get_appeals: REQUEST_INITIAL_STATE,
    get_appeals_notifications: REQUEST_INITIAL_STATE,
    get_notifications_unread: REQUEST_INITIAL_STATE,
    get_monitored_electronic_trading_ids: REQUEST_INITIAL_STATE,
  },
};

const reducer: Reducer<MonitoringState> = (
  state = INITIAL_STATE,
  { type, result, rest, lotId, biddingId, page, sourceId, companyId, monitor },
) => {
  return produce(state, (draft) => {
    const electronicTradingId = rest || 0;
    const lot = lotId || null;
    switch (type) {
      case MonitoringRequestTypes.STATUS_MONITORING_REQUEST:
        draft.request.monitoring_status = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;

      case MonitoringRequestTypes.STATUS_MONITORING_SUCCESS:
        draft.monitoring.monitoring_status = result.data.monitoring_status;
        draft.request.monitoring_status = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;

      case MonitoringRequestTypes.STATUS_MONITORING_FAILURE:
        draft.request.monitoring_status = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;

      case MonitoringRequestTypes.ELECTRONICS_TRADINGS_REQUEST:
        draft.monitoring.current_filter_type = rest?.filter_type || 'all';
        draft.request.electronics_tradings = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;

      case MonitoringRequestTypes.ELECTRONICS_TRADINGS_SUCCESS:
        draft.request.electronics_tradings = {
          loading: false,
          loaded: true,
          error: false,
        };
        draft.monitoring.electronics_tradings = result.data.electronics_trading;
        draft.monitoring.electronics_tradings_total_entries = result.data.total_entries;
        draft.monitoring.electronics_tradings_total_pages = result.data.total_pages;
        draft.monitoring.monitored_source_id_list = result.data.monitored_source_id_list;
        break;

      case MonitoringRequestTypes.ELECTRONICS_TRADINGS_FAILURE:
        draft.request.electronics_tradings = {
          loading: false,
          loaded: false,
          error: true,
        };
        draft = INITIAL_STATE;
        break;

      case MonitoringRequestTypes.ELECTRONIC_TRADING_REQUEST:
        draft.request.electronics_tradings = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;

      case MonitoringRequestTypes.ELECTRONIC_TRADING_SUCCESS:
        draft.request.electronics_tradings = {
          loading: false,
          loaded: true,
          error: false,
        };
        draft.monitoring.electronics_tradings = _.uniq(
          [...state.monitoring.electronics_tradings, ...result.data.electronics_trading],
          'id',
        );
        draft.monitoring.electronics_tradings_total_entries = result.data.total_entries;
        draft.monitoring.electronics_tradings_total_pages = result.data.total_pages;
        draft.monitoring.monitored_source_id_list = result.data.monitored_source_id_list;
        break;

      case MonitoringRequestTypes.ELECTRONIC_TRADING_FAILURE:
        draft.request.electronics_tradings = {
          loading: false,
          loaded: false,
          error: true,
        };
        draft = INITIAL_STATE;
        break;

      case MonitoringRequestTypes.DESTROY_ELECTRONICS_TRADINGS_REQUEST:
        draft.request.delete_electronic_trading = {
          ...INITIAL_STATE.request.delete_electronic_trading,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.DESTROY_ELECTRONICS_TRADINGS_SUCCESS:
        if (state.monitoring.filtered_by !== 'by_all') {
          if (
            state.monitoring.electronics_tradings?.length === 1 &&
            state.monitoring.current_page > 1
          )
            draft.monitoring.current_page = state.monitoring.current_page - 1;

          if (
            state.monitoring.electronics_tradings?.length === 1 &&
            state.monitoring.current_page === 1
          )
            draft.monitoring.current_source_id = [];

          const electronicsTradingList = state.monitoring.electronics_tradings.filter(
            (electronicTrading) => electronicTrading.bidding_id !== biddingId,
          );
          draft.monitoring.electronics_tradings = electronicsTradingList;
        }
        draft.request.delete_electronic_trading = {
          ...INITIAL_STATE.request.delete_electronic_trading,
          loaded: true,
        };
        break;

      case MonitoringRequestTypes.DESTROY_ELECTRONICS_TRADINGS_FAILURE:
        draft.request.delete_electronic_trading = {
          ...INITIAL_STATE.request.delete_electronic_trading,
          error: true,
        };
        break;

      case MonitoringRequestTypes.TRADING_MESSAGES_REQUEST:
        draft.request.monitoring_messages = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;

      case MonitoringRequestTypes.TRADING_MESSAGES_SUCCESS:
        if (!result) return;
        draft.monitoring.monitoring_messages[result.data.electronic_trading_id] =
          result.data.trading_messages;
        draft.request.monitoring_messages = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;

      case MonitoringRequestTypes.TRADING_MESSAGES_FAILURE:
        draft.request.monitoring_messages = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;

      case MonitoringRequestTypes.UPDATE_TRADING_MESSAGES:
        const messageList = _.sortBy(
          _.uniq(
            [...(state.monitoring.monitoring_messages[result.electronic_trading_id] || []), result],
            'id',
          ),
          (message: TradingMessageTypes) => {
            return Date.parse(message.message_time as string);
          },
        );

        draft.monitoring.monitoring_messages[result.electronic_trading_id] = messageList;
        const electronicsTrading = state.monitoring.electronics_tradings;
        const receivedMessageElectronicTradingIndex = electronicsTrading.findIndex(
          (electronicTrading) => electronicTrading.id === result.electronic_trading_id,
        );

        const receivedMessageElectronicTrading =
          electronicsTrading[receivedMessageElectronicTradingIndex];

        const updatedReceivedMessageElectronicTrading = {
          ...receivedMessageElectronicTrading,
          last_message_time: result.message_time,
        };

        draft.monitoring.electronics_tradings[receivedMessageElectronicTradingIndex] =
          updatedReceivedMessageElectronicTrading;

        break;

      case MonitoringRequestTypes.INTEREST_LOTS_REQUEST:
        if (!draft.monitoring.interest_lots)
          draft.monitoring.interest_lots = INITIAL_STATE.monitoring.interest_lots;
        draft.request.interest_lots = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;

      case MonitoringRequestTypes.INTEREST_LOTS_SUCCESS:
        if (!result) return;
        draft.monitoring.interest_lots[`${electronicTradingId}_${companyId}`] = result.data?.data
          ? result.data?.data
          : result.data;
        draft.request.interest_lots = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;

      case MonitoringRequestTypes.INTEREST_LOTS_FAILURE:
        draft.request.interest_lots = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;

      case MonitoringRequestTypes.SAVE_INTEREST_LOTS_REQUEST:
        draft.request.interest_lots = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;

      case MonitoringRequestTypes.SAVE_INTEREST_LOTS_SUCCESS:
        draft.monitoring.interest_lots[`${electronicTradingId}_${companyId}`] = result.data?.data
          ? result.data?.data
          : result.data;
        draft.request.interest_lots = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;

      case MonitoringRequestTypes.SAVE_INTEREST_LOTS_FAILURE:
        draft.request.interest_lots = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;

      case MonitoringRequestTypes.GET_TRADING_KEYWORDS_REQUEST:
        draft.request.get_trading_keywords = {
          ...INITIAL_STATE.request.get_trading_keywords,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.GET_TRADING_KEYWORDS_SUCCESS:
        if (!result) return;

        draft.monitoring.trading_keywords = {
          ...state.monitoring.trading_keywords,
          [companyId]: result.data.trading_keywords,
        };
        draft.request.get_trading_keywords = {
          ...INITIAL_STATE.request.get_trading_keywords,
          loaded: true,
        };
        break;

      case MonitoringRequestTypes.GET_TRADING_KEYWORDS_FAILURE:
        draft.request.get_trading_keywords = {
          ...INITIAL_STATE.request.get_trading_keywords,
          error: true,
        };
        break;

      case MonitoringRequestTypes.SAVE_TRADING_KEYWORDS_REQUEST:
        draft.request.save_trading_keywords = {
          ...INITIAL_STATE.request.save_trading_keywords,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.SAVE_TRADING_KEYWORDS_SUCCESS:
        draft.monitoring.trading_keywords[companyId] = result.data.trading_keywords;
        draft.request.save_trading_keywords = {
          ...INITIAL_STATE.request.save_trading_keywords,
          loaded: true,
        };
        break;

      case MonitoringRequestTypes.SAVE_TRADING_KEYWORDS_FAILURE:
        draft.request.save_trading_keywords = {
          ...INITIAL_STATE.request.save_trading_keywords,
          error: true,
        };
        break;

      case MonitoringRequestTypes.DELETE_TRADING_KEYWORDS_REQUEST:
        draft.request.delete_trading_keywords = {
          ...INITIAL_STATE.request.delete_trading_keywords,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.DELETE_TRADING_KEYWORDS_SUCCESS:
        draft.monitoring.trading_keywords[companyId] = result.data.trading_keywords;
        draft.request.delete_trading_keywords = {
          ...INITIAL_STATE.request.delete_trading_keywords,
          loaded: true,
        };
        break;

      case MonitoringRequestTypes.UPDATE_TRADING_KEYWORD_SUCCESS:
        if (Array.isArray(result?.data?.trading_keywords)) {
          draft.monitoring.trading_keywords[companyId] = result.data.trading_keywords;
        }
        break;

      case MonitoringRequestTypes.DELETE_TRADING_KEYWORDS_FAILURE:
        draft.request.delete_trading_keywords = {
          ...INITIAL_STATE.request.delete_trading_keywords,
          error: true,
        };
        break;

      case MonitoringRequestTypes.MARK_AS_READ_SUCCESS:
        const electronicTradingsList = [...state.monitoring.electronics_tradings];
        const electronicTradingIndex = electronicTradingsList.findIndex(
          (electronicTrading) => electronicTrading.id === electronicTradingId,
        );

        const electronicTrading = { ...electronicTradingsList[electronicTradingIndex] };
        electronicTrading.have_new_messages = false;

        if (lot) {
          const haveNewMessagesLot = electronicTrading?.have_new_messages_lots.filter(
            (haveNewMessageLot) => ![lot, 0].includes(haveNewMessageLot),
          );
          electronicTrading.have_new_messages_lots = haveNewMessagesLot;
        }

        electronicTradingsList[electronicTradingIndex] = electronicTrading;
        draft.monitoring.electronics_tradings = electronicTradingsList;
        break;

      case MonitoringRequestTypes.GET_APPEALS_REQUEST:
        draft.request.get_appeals = { ...INITIAL_STATE.request.get_appeals, loading: true };
        break;

      case MonitoringRequestTypes.GET_APPEALS_SUCCESS:
        draft.monitoring.appeals[electronicTradingId as string] = result.data?.data
          ? result.data?.data
          : result.data;
        draft.request.get_appeals = { ...INITIAL_STATE.request.get_appeals, loaded: true };
        break;

      case MonitoringRequestTypes.GET_APPEALS_FAILURE:
        draft.request.get_appeals = { ...INITIAL_STATE.request.get_appeals, error: true };
        break;

      case MonitoringRequestTypes.GET_APPEALS_NOTIFICATIONS_REQUEST:
        draft.request.get_appeals_notifications = {
          ...INITIAL_STATE.request.get_appeals_notifications,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.GET_APPEALS_NOTIFICATIONS_SUCCESS:
        if (!result) return;
        draft.monitoring.appeals_notifications = result.data.notifications;
        draft.request.get_appeals_notifications = {
          ...INITIAL_STATE.request.get_appeals_notifications,
          loaded: true,
        };
        break;

      case MonitoringRequestTypes.GET_APPEALS_NOTIFICATIONS_FAILURE:
        draft.request.get_appeals_notifications = {
          ...INITIAL_STATE.request.get_appeals_notifications,
          error: true,
        };
        break;

      case MonitoringRequestTypes.GET_NOTIFICATIONS_UNREAD_REQUEST:
        draft.request.get_notifications_unread = {
          ...INITIAL_STATE.request.get_notifications_unread,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.GET_NOTIFICATIONS_UNREAD_SUCCESS:
        draft.monitoring.notifications_unread = result?.data?.has_notification;
        draft.request.get_notifications_unread = {
          ...INITIAL_STATE.request.get_notifications_unread,
          loaded: true,
        };
        break;

      case MonitoringRequestTypes.GET_NOTIFICATIONS_UNREAD_FAILURE:
        draft.request.get_notifications_unread = {
          ...INITIAL_STATE.request.get_notifications_unread,
          error: true,
        };
        break;

      case MonitoringRequestTypes.MARK_APPEAL_AS_READ_SUCCESS:
        draft.monitoring.appeals_notifications = state.monitoring.appeals_notifications.filter(
          (appealNotification) => appealNotification.electronic_trading_id !== electronicTradingId,
        );
        break;

      case MonitoringRequestTypes.GET_MONITORED_ELECTRONIC_TRADING_IDS_REQUEST:
        draft.request.get_monitored_electronic_trading_ids = {
          ...INITIAL_STATE.request.get_monitored_electronic_trading_ids,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.GET_MONITORED_ELECTRONIC_TRADING_IDS_SUCCESS:
        draft.monitoring.monitored_electronic_tradings =
          result.data.electronics_trading_by_companies;
        draft.request.get_monitored_electronic_trading_ids = {
          ...INITIAL_STATE.request.get_monitored_electronic_trading_ids,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.GET_MONITORED_ELECTRONIC_TRADING_IDS_FAILURE:
        draft.request.get_monitored_electronic_trading_ids = {
          ...INITIAL_STATE.request.get_monitored_electronic_trading_ids,
          loading: true,
        };
        break;

      case MonitoringRequestTypes.DISABLE_NOTIFICATIONS:
        draft.monitoring.disable_notifications = true;
        break;

      case MonitoringRequestTypes.ENABLE_NOTIFICATIONS:
        draft.monitoring.disable_notifications = false;
        break;

      case MonitoringRequestTypes.HANDLE_FILTER_BY:
        if (state.monitoring.filtered_by === 'by_me') draft.monitoring.filtered_by = 'by_all';
        if (state.monitoring.filtered_by === 'by_all') draft.monitoring.filtered_by = 'by_me';

        break;

      case MonitoringRequestTypes.ADD_MONITORED_BY:
        draft.monitoring.electronics_tradings = state.monitoring.electronics_tradings.map((et) => {
          if (et.bidding_id !== biddingId) return et;
          const alreadyExists = et.monitored_by?.some((m) => m.id === monitor?.id);
          if (alreadyExists) return et;
          return { ...et, monitored_by: [...(et.monitored_by || []), monitor] };
        });
        break;

      case MonitoringRequestTypes.REMOVE_MONITORED_BY:
        draft.monitoring.electronics_tradings = state.monitoring.electronics_tradings.map((et) => {
          if (et.bidding_id !== biddingId) return et;
          return {
            ...et,
            monitored_by: et.monitored_by?.filter((m) => m.id !== monitor?.id) || [],
          };
        });
        break;

      case MonitoringRequestTypes.UPDATE_STORE_FIELDS:
        if (!state.monitoring.monitoring_status)
          draft.monitoring.monitoring_status = INITIAL_STATE.monitoring.monitoring_status;

        if (!state.monitoring.electronics_tradings)
          draft.monitoring.electronics_tradings = INITIAL_STATE.monitoring.electronics_tradings;

        if (!state.monitoring.monitoring_messages)
          draft.monitoring.monitoring_messages = INITIAL_STATE.monitoring.monitoring_messages;

        if (!state.monitoring.interest_lots)
          draft.monitoring.interest_lots = INITIAL_STATE.monitoring.interest_lots;

        if (!state.monitoring.trading_keywords)
          draft.monitoring.trading_keywords = INITIAL_STATE.monitoring.trading_keywords;

        if (!state.monitoring.appeals) draft.monitoring.appeals = INITIAL_STATE.monitoring.appeals;

        if (!state.monitoring.appeals_notifications)
          draft.monitoring.appeals_notifications = INITIAL_STATE.monitoring.appeals_notifications;

        if (!state.monitoring.notifications_unread)
          draft.monitoring.notifications_unread = INITIAL_STATE.monitoring.notifications_unread;

        if (!state.monitoring.disable_notifications)
          draft.monitoring.disable_notifications = INITIAL_STATE.monitoring.disable_notifications;

        if (!state.monitoring.electronics_tradings_total_entries)
          draft.monitoring.electronics_tradings_total_entries =
            INITIAL_STATE.monitoring.electronics_tradings_total_entries;

        if (!state.monitoring.electronics_tradings_total_pages)
          draft.monitoring.electronics_tradings_total_pages =
            INITIAL_STATE.monitoring.electronics_tradings_total_pages;

        if (!state.monitoring.monitored_source_id_list)
          draft.monitoring.monitored_source_id_list =
            INITIAL_STATE.monitoring.monitored_source_id_list;

        if (!state.monitoring.current_page)
          draft.monitoring.current_page = INITIAL_STATE.monitoring.current_page;

        if (!state.monitoring.current_source_id)
          draft.monitoring.current_source_id = INITIAL_STATE.monitoring.current_source_id;

        if (!state.monitoring.filtered_by)
          draft.monitoring.filtered_by = INITIAL_STATE.monitoring.filtered_by;

        if (!state.request.monitoring_status)
          draft.request.monitoring_status = INITIAL_STATE.request.monitoring_status;

        if (!state.request.electronics_tradings)
          draft.request.electronics_tradings = INITIAL_STATE.request.electronics_tradings;

        if (!state.request.monitoring_messages)
          draft.request.monitoring_messages = INITIAL_STATE.request.monitoring_messages;

        if (!state.request.interest_lots)
          draft.request.interest_lots = INITIAL_STATE.request.interest_lots;

        if (!state.request.get_trading_keywords)
          draft.request.get_trading_keywords = INITIAL_STATE.request.get_trading_keywords;

        if (!state.request.save_trading_keywords)
          draft.request.save_trading_keywords = INITIAL_STATE.request.save_trading_keywords;

        if (!state.request.delete_trading_keywords)
          draft.request.delete_trading_keywords = INITIAL_STATE.request.delete_trading_keywords;

        if (!state.request.get_appeals)
          draft.request.get_appeals = INITIAL_STATE.request.get_appeals;

        if (!state.request.get_appeals_notifications)
          draft.request.get_appeals_notifications = INITIAL_STATE.request.get_appeals_notifications;

        if (!state.request.get_notifications_unread)
          draft.request.get_notifications_unread = INITIAL_STATE.request.get_notifications_unread;
        break;
      case MonitoringRequestTypes.ARCHIVE_SUCCESS: {
        const { bidding_id, archived: archivedValue, important: importantValue } = result.data;
        const currentTab = state.monitoring.current_filter_type;
        const isByAll = state.monitoring.filtered_by === 'by_all';
        const shouldRemove =
          (currentTab === 'archived' && !archivedValue) ||
          (!isByAll && currentTab !== 'archived' && archivedValue);

        if (shouldRemove) {
          draft.monitoring.electronics_tradings = state.monitoring.electronics_tradings.filter(
            (item) => item.bidding_id !== bidding_id,
          );
        } else {
          draft.monitoring.electronics_tradings = state.monitoring.electronics_tradings.map(
            (item) =>
              item.bidding_id === bidding_id
                ? { ...item, archived: archivedValue, important: importantValue }
                : item,
          );
        }

        break;
      }
      case MonitoringRequestTypes.TOGGLE_IMPORTANT_SUCCESS: {
        const { important: importantVal } = result.data;
        const currentImportantTab = state.monitoring.current_filter_type;
        const shouldRemoveImportant = currentImportantTab === 'important' && !importantVal;

        if (shouldRemoveImportant) {
          draft.monitoring.electronics_tradings = state.monitoring.electronics_tradings.filter(
            (item) => item.bidding_id !== biddingId,
          );
        } else {
          draft.monitoring.electronics_tradings = state.monitoring.electronics_tradings.map(
            (item) => (item.bidding_id === biddingId ? { ...item, important: importantVal } : item),
          );
        }

        break;
      }
      case MonitoringRequestTypes.UPDATE_CURRENT_PAGE:
        draft.monitoring.current_page = page;
        break;

      case MonitoringRequestTypes.UPDATE_CURRENT_SOURCE_ID:
        draft.monitoring.current_source_id = sourceId;
        break;
      default:
    }
  });
};

export default reducer;
